import {
  RequirementExpirationStrategy,
  RequirementReviewPolicy,
  RequirementType,
  SharedFileRequestContextType
} from "@wingspanhq/wingspan-api-sdk";

export const REQUIREMENTS_WITH_ERRORS: Partial<
  Record<RequirementType, boolean>
> = {
  [RequirementType.DOCUMENT_UPLOAD]: false,
  [RequirementType.SIGNATURE]: false,
  [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: true,
  [RequirementType.BACKGROUND_CHECK]: true,
  [RequirementType.INSURANCE_COVERAGE]: true,
  [RequirementType.REGISTRATION]: false,
  [RequirementType.TAX_VERIFICATION]: false,
  [RequirementType.PAYOUT_METHOD]: false
};

export const convertFormApprovalDataToReviewStrategy = (payload: {
  isApprovalRequired: boolean;
  reviewStrategy?: RequirementReviewPolicy;
  type: RequirementType;
  contextType?: SharedFileRequestContextType;
}): RequirementReviewPolicy => {
  const simpleReviewStrategy = payload.isApprovalRequired
    ? RequirementReviewPolicy.ALWAYS_REQUIRED
    : RequirementReviewPolicy.NEVER_REQUIRED;

  const withErrorsReviewStrategy = payload.reviewStrategy
    ? payload.reviewStrategy
    : RequirementReviewPolicy.NEVER_REQUIRED;

  // Shared files also require to look at context type to determine the review stratergy
  if (
    payload.type === RequirementType.DOCUMENT_UPLOAD &&
    payload.contextType !== SharedFileRequestContextType.NONE
  ) {
    return withErrorsReviewStrategy;
  }

  if (REQUIREMENTS_WITH_ERRORS[payload.type]) {
    return withErrorsReviewStrategy;
  } else {
    return simpleReviewStrategy;
  }
};

export const getReviewStrategyDefaultValues = (
  withErrorsReviewStrategy: boolean
):
  | { isApprovalRequired: boolean }
  | { reviewStrategy: RequirementReviewPolicy } => {
  if (withErrorsReviewStrategy) {
    return {
      reviewStrategy: RequirementReviewPolicy.ON_FAIL
    };
  } else {
    return {
      isApprovalRequired: false
    };
  }
};

export const getExpirationStrategyDefaultValues = (
  withExpirationStrategy?: boolean
): {
  expirationStrategy?: RequirementExpirationStrategy;
  expirationDays?: number;
  manualRenewalAllowed: boolean;
} => {
  if (withExpirationStrategy) {
    return {
      expirationStrategy: RequirementExpirationStrategy.NONE,
      expirationDays: 30,
      manualRenewalAllowed: false
    };
  } else {
    return {
      manualRenewalAllowed: false
    };
  }
};

export const convertFormDataToExpirationStrategy = (payload: {
  expirationStrategy?: RequirementExpirationStrategy;
  expirationDays?: number;
  manualRenewalAllowed?: boolean;
}): {
  expirationStrategy?: RequirementExpirationStrategy;
  expirationDays?: number;
  manualRenewalAllowed: boolean;
} => {
  if (
    !payload.expirationStrategy ||
    payload.expirationStrategy === RequirementExpirationStrategy.NONE
  ) {
    return {
      manualRenewalAllowed: !!payload.manualRenewalAllowed,
      expirationStrategy: RequirementExpirationStrategy.NONE
    };
  }

  return {
    expirationStrategy: payload.expirationStrategy,
    expirationDays: payload.expirationDays,
    manualRenewalAllowed: !!payload.manualRenewalAllowed
  };
};

export const REVIEW_STRATEGY_ON_FAIL_COPY =
  "Only require when there are flags for review or validations fail (auto-approve if passed)";
export const REVIEW_STRATEGY_ALWAYS_REQUIRED_COPY =
  "Always require (every submission)";
export const REVIEW_STRATEGY_NEVER_REQUIRED_COPY =
  "Never require (approval step is skipped)";
